import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Zap, Shield, MapPin, Star, Check, Users, CheckCircle2, DollarSign, Facebook, Instagram } from "lucide-react";
import { Logo } from "../components/Logo";
import LangToggle from "../components/LangToggle";
import ShareApp from "../components/ShareApp";
import { api, CATEGORIES } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useI18n } from "../lib/i18n";

export default function Home() {
  const { user } = useAuth();
  const { t } = useI18n();
  const [stats, setStats] = useState({ tasks_completed: 0, active_helpers: 0, avg_rating: 0, reviews_count: 0 });
  useEffect(() => { api.get("/public/stats").then((r) => setStats(r.data)).catch(() => {}); }, []);
  const nextPath = user
    ? user.role === "admin" ? "/admin"
      : user.role === "helper" ? "/dashboard/helper" : "/dashboard/client"
    : "/register";

  return (
    <div className="min-h-screen bg-canvas">
      {/* Core message banner — first thing user sees */}
      <div className="bg-navy text-white relative overflow-hidden noise" data-testid="core-message-banner">
        <div className="absolute -top-12 -right-12 h-40 w-40 rounded-full bg-lime/20 blur-3xl" />
        <div className="relative max-w-6xl mx-auto px-4 md:px-8 py-5 md:py-7 flex items-center gap-4">
          <p className="font-heading font-black tracking-tight text-xl sm:text-2xl md:text-3xl leading-tight text-white flex-1">
            {t("hero.core_message").split("Propose ton prix.").length > 1 || t("hero.core_message").split("Set your price.").length > 1 ? (
              <>
                {t("hero.core_message").split(/Propose ton prix\.|Set your price\./)[0]}
                <span className="text-lime">
                  {t("hero.core_message").includes("Propose ton prix.") ? "Propose ton prix." : "Set your price."}
                </span>
                {t("hero.core_message").split(/Propose ton prix\.|Set your price\./)[1]}
              </>
            ) : t("hero.core_message")}
          </p>
        </div>
      </div>

      {/* Top bar */}
      <header className="sticky top-0 z-40 bg-white/90 backdrop-blur-xl border-b border-[#E2E8F0]/70">
        <div className="max-w-6xl mx-auto px-4 md:px-8 h-16 flex items-center justify-between">
          <Logo size={38} />
          <div className="flex items-center gap-2">
            <ShareApp variant="icon" />
            <LangToggle />
            {user ? (
              <Link to={nextPath} className="btn-primary py-2 px-4 text-sm" data-testid="home-go-dashboard">{t("nav.my_dashboard")}</Link>
            ) : (
              <Link to="/login" className="text-navy font-bold text-sm px-3 py-2 hover:underline" data-testid="home-login">{t("nav.login")}</Link>
            )}
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-white via-surface to-white" />
        <div className="absolute -top-24 -right-24 h-72 w-72 rounded-full bg-lime/30 blur-3xl" />
        <div className="absolute -bottom-24 -left-24 h-72 w-72 rounded-full bg-navy/10 blur-3xl" />
        <div className="relative max-w-6xl mx-auto px-4 md:px-8 py-16 md:py-28 grid md:grid-cols-2 gap-12 items-center">
          <div>
            <span className="chip-lime mb-6" data-testid="hero-tagline">{t("hero.tagline")}</span>
            <h1 className="text-5xl sm:text-6xl md:text-7xl font-heading font-black tracking-tighter leading-[0.95] text-navy">
              {t("hero.title_1")}<br />
              <span className="bg-lime px-3 -ml-1 inline-block mt-2 rounded-md">{t("hero.title_helper")}</span><br />
              {t("hero.title_near")}
            </h1>
            <p className="mt-6 text-lg text-[#475569] max-w-md font-medium">
              {t("hero.subtitle")}
            </p>
            <div className="mt-8 flex flex-col gap-3 max-w-md">
              <Link to={user ? nextPath : "/register?role=client"} className="btn-primary px-10 py-5 text-lg shadow-xl shadow-lime/30 ring-4 ring-lime/20 justify-center" data-testid="hero-cta-client">
                {t("hero.cta_publish")} <ArrowRight size={22} />
              </Link>
              <Link
                to={user ? nextPath : "/register?role=helper"}
                className="bg-navy hover:bg-[#0E2440] text-lime font-black px-10 py-4 rounded-full text-base inline-flex items-center justify-center gap-2 shadow-lg shadow-navy/20 border-2 border-navy transition-all hover:scale-[1.02]"
                data-testid="hero-cta-helper"
              >
                Devenir helper <ArrowRight size={18} />
              </Link>
            </div>
            <div className="mt-3">
              <Link to="/idees" className="text-navy/70 font-semibold text-sm inline-flex items-center gap-1 hover:text-navy underline-offset-4 hover:underline" data-testid="hero-ideas-link">
                💡 Découvrir 80+ idées de tâches <ArrowRight size={14} />
              </Link>
            </div>
            <div className="mt-8 flex items-center gap-6 text-sm text-[#475569] font-semibold">
              <span className="flex items-center gap-2"><Shield size={16} className="text-lime" /> {t("hero.trust_payment")}</span>
              <span className="flex items-center gap-2"><MapPin size={16} className="text-lime" /> {t("hero.trust_local")}</span>
              <span className="flex items-center gap-2"><Zap size={16} className="text-lime" /> {t("hero.trust_fast")}</span>
            </div>
          </div>

          <div className="relative">
            <div className="relative rounded-3xl overflow-hidden shadow-xl border-4 border-navy">
              <img
                src="/helpers/delivery.png"
                alt="Helper Go Helper en uniforme officiel avec casquette GH livrant un colis"
                className="w-full h-auto block"
                data-testid="hero-helper-photo"
              />
              <span className="absolute top-4 left-4 chip-lime shadow-lg">Helper officiel</span>
            </div>
            <div className="card-surface p-4 -mt-6 mx-6 relative z-10 flex items-center gap-3">
              <div className="h-10 w-10 rounded-full bg-navy text-lime font-black flex items-center justify-center">M</div>
              <div className="flex-1">
                <div className="font-bold text-navy">Marc L. · Helper Pro</div>
                <div className="text-xs flex items-center gap-1 text-[#475569]"><Star size={12} className="fill-lime text-lime" /> 4.9 · 52 tâches</div>
              </div>
              <span className="chip-lime">Fiable</span>
            </div>
          </div>
        </div>
      </section>

      {/* Social proof stats band */}
      <section className="max-w-6xl mx-auto px-4 md:px-8 -mt-6 md:-mt-10 relative z-10" data-testid="stats-band">
        <div className="card-surface p-4 md:p-6 grid grid-cols-3 gap-3 md:gap-6 divide-x divide-[#E2E8F0]">
          <Stat icon={<CheckCircle2 className="text-lime" size={18} />} value={stats.tasks_completed} suffix="" label={t("stats.tasks_done")} testid="stat-tasks" />
          <Stat icon={<Users className="text-lime" size={18} />} value={stats.active_helpers} suffix="" label={t("stats.active_helpers")} testid="stat-helpers" />
          <Stat icon={<Star className="text-lime fill-lime" size={18} />} value={stats.avg_rating || 5.0} suffix="/5" label={stats.reviews_count ? `${stats.reviews_count} ${t("stats.reviews")}` : t("stats.rating")} testid="stat-rating" />
        </div>
      </section>

      {/* Helper gallery — real uniformed helpers */}
      <section className="max-w-6xl mx-auto px-4 md:px-8 py-12" data-testid="helper-gallery">
        <div className="flex items-end justify-between mb-6">
          <div>
            <span className="text-xs font-black uppercase tracking-[0.2em] text-navy/60">Nos helpers</span>
            <h2 className="text-3xl md:text-4xl font-heading font-black text-navy mt-1">
              En casquette et uniforme <span className="text-lime">officiel</span>.
            </h2>
            <p className="text-[#475569] mt-2 max-w-xl">Reconnaissables dès le premier coup d'œil. Un t-shirt navy, une casquette GH, et une volonté d'aider.</p>
          </div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
          {[
            { src: "/helpers/grocery.png", label: "Courses & épicerie" },
            { src: "/helpers/furniture.png", label: "Montage meubles" },
            { src: "/helpers/delivery.png", label: "Livraison colis" },
            { src: "/helpers/snow.png", label: "Déneigement" },
          ].map((h) => (
            <div key={h.src} className="card-interactive overflow-hidden" data-testid={`helper-${h.label}`}>
              <div className="aspect-[3/4] overflow-hidden bg-surface">
                <img src={h.src} alt={`Helper ${h.label}`} className="w-full h-full object-cover" />
              </div>
              <div className="p-3">
                <div className="font-bold text-navy text-sm">{h.label}</div>
                <div className="text-xs text-[#475569] mt-0.5">Helper disponible</div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Message box — "n'est pas un emploi" */}
      <section className="max-w-6xl mx-auto px-4 md:px-8 py-8">
        <div className="bg-navy text-white rounded-3xl p-8 md:p-12 relative overflow-hidden noise" data-testid="not-a-job-banner">
          <div className="absolute -top-16 -right-16 h-56 w-56 rounded-full bg-lime/20 blur-3xl" />
          <div className="relative">
            <span className="chip-lime mb-4">À retenir</span>
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-black leading-tight text-white">
              <span className="text-white">{t("notjob.title_brand")}</span> <span className="text-lime">{t("notjob.title_rest")}</span>
            </h2>
            <p className="mt-4 text-lg md:text-xl font-medium text-white/90 max-w-3xl leading-relaxed">
              {t("notjob.body_1")} <span className="text-lime font-bold">{t("notjob.body_accent")}</span> {t("notjob.body_2")}
            </p>
          </div>
        </div>
      </section>

      {/* Categories */}
      <section className="max-w-6xl mx-auto px-4 md:px-8 py-12">
        <div className="flex items-end justify-between mb-8">
          <div>
            <span className="text-xs font-black uppercase tracking-[0.2em] text-navy/60">Catégories</span>
            <h2 className="text-3xl md:text-4xl font-heading font-black text-navy mt-1">Ce qu'on peut faire pour toi</h2>
          </div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
          {CATEGORIES.map((c) => (
            <Link key={c.id} to={user ? "/tasks/new" : "/register"} className="card-interactive p-5" data-testid={`cat-${c.id}`}>
              <div className="text-xs font-black uppercase tracking-wider text-[#475569]">Catégorie</div>
              <div className="font-heading font-black text-navy text-xl mt-1">{c.label}</div>
              <div className="mt-3 text-lime font-bold text-sm inline-flex items-center gap-1">Publier <ArrowRight size={14} /></div>
            </Link>
          ))}
        </div>
      </section>

      {/* How it works */}
      <section className="max-w-6xl mx-auto px-4 md:px-8 py-16">
        <span className="text-xs font-black uppercase tracking-[0.2em] text-navy/60">Comment ça marche</span>
        <h2 className="text-3xl md:text-4xl font-heading font-black text-navy mt-1 mb-10">3 étapes. C'est tout.</h2>
        <div className="grid md:grid-cols-3 gap-6">
          {[
            { n: "01", t: "Publie ta tâche", d: "Décris ce dont tu as besoin, ajoute des photos et propose ton prix." },
            { n: "02", t: "Un helper accepte", d: "Quelqu'un près de chez toi accepte. Tu paies via Go Helper en toute sécurité." },
            { n: "03", t: "Confirme et évalue", d: "Une fois la tâche faite, confirme. Le helper reçoit son paiement." },
          ].map((s) => (
            <div key={s.n} className="card-surface p-6">
              <div className="font-mono font-bold text-lime text-sm">{s.n}</div>
              <div className="font-heading font-black text-navy text-2xl mt-2">{s.t}</div>
              <p className="text-[#475569] mt-2">{s.d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Pricing example */}
      <section className="max-w-6xl mx-auto px-4 md:px-8 py-12">
        <div className="card-surface p-6 md:p-10 grid md:grid-cols-2 gap-8">
          <div>
            <span className="text-xs font-black uppercase tracking-[0.2em] text-navy/60">Tarification transparente</span>
            <h2 className="text-3xl md:text-4xl font-heading font-black text-navy mt-1">Tu vois tout avant de payer.</h2>
            <ul className="mt-6 space-y-2 text-[#475569] font-medium">
              <li className="flex gap-2"><Check size={18} className="text-lime shrink-0" /> Frais fixe 3 $ + commission 15 %</li>
              <li className="flex gap-2"><Check size={18} className="text-lime shrink-0" /> Option urgence +30 % (si besoin rapide)</li>
              <li className="flex gap-2"><Check size={18} className="text-lime shrink-0" /> Paiement gardé en fiducie, libéré après confirmation</li>
            </ul>
          </div>
          <div className="bg-surface rounded-2xl p-6 font-mono text-sm">
            <div className="flex justify-between py-1"><span>Prix de base</span><span className="font-bold">30,00 $</span></div>
            <div className="flex justify-between py-1"><span>Urgence (+30 %)</span><span className="font-bold">9,00 $</span></div>
            <div className="flex justify-between py-1 border-t border-[#E2E8F0] mt-2 pt-2"><span>Sous-total helper</span><span className="font-bold">39,00 $</span></div>
            <div className="flex justify-between py-1"><span>Frais Go Helper</span><span className="font-bold">8,85 $</span></div>
            <div className="flex justify-between py-2 border-t border-[#E2E8F0] mt-2 text-navy"><span className="font-black">Total client</span><span className="font-black text-xl">47,85 $</span></div>
          </div>
        </div>
      </section>

      {/* Helper earnings — "Combien tu gagnes ?" */}
      <section className="max-w-6xl mx-auto px-4 md:px-8 py-12" data-testid="helper-earnings">
        <div className="bg-navy text-white rounded-3xl p-6 md:p-10 relative overflow-hidden noise">
          <div className="absolute -top-20 -right-20 h-60 w-60 rounded-full bg-lime/20 blur-3xl" />
          <div className="relative grid md:grid-cols-2 gap-8 items-center">
            <div>
              <span className="chip-lime">Pour les helpers</span>
              <h2 className="text-3xl md:text-5xl font-heading font-black text-white mt-4">
                Combien tu <span className="text-lime">gagnes</span> ?
              </h2>
              <p className="text-white/80 mt-3 text-lg">Le montant proposé par le client, c'est <strong className="text-lime">ce que tu reçois</strong>. Simple, clair, sans surprise.</p>
              <Link to={user ? nextPath : "/register?role=helper"} className="btn-primary mt-6 inline-flex" data-testid="earnings-cta">
                Devenir helper <ArrowRight size={18} />
              </Link>
            </div>
            <div className="space-y-3">
              {[
                { cat: "Déneigement", base: 25, time: "30 min" },
                { cat: "Livraison colis", base: 20, time: "20 min" },
                { cat: "Montage meubles", base: 60, time: "1h30" },
              ].map((e, i) => (
                <div key={i} className="bg-white/10 backdrop-blur rounded-xl p-4 flex items-center justify-between">
                  <div>
                    <div className="font-bold text-white">{e.cat}</div>
                    <div className="text-xs text-white/60">≈ {e.time}</div>
                  </div>
                  <div className="flex items-center gap-2 text-lime font-heading font-black">
                    <DollarSign size={18} />
                    <span className="text-2xl">{e.base} $</span>
                  </div>
                </div>
              ))}
              <p className="text-xs text-white/50 text-center">Exemples. Le prix final est toujours fixé par le client.</p>
            </div>
          </div>
        </div>
      </section>

      <footer className="border-t border-[#E2E8F0] py-10 mt-10">
        <div className="max-w-6xl mx-auto px-4 md:px-8 flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="flex flex-col items-center md:items-start gap-2">
            <Logo size={32} />
            <p className="text-sm text-[#475569]">{t("footer.tagline")}</p>
          </div>
          <div className="flex items-center gap-3 flex-wrap justify-center">
            <div className="flex items-center gap-2">
              <span className="text-sm font-bold text-navy mr-1">{t("nav.follow_us")}</span>
              <a
                href="https://www.facebook.com/share/18mKMi1XR1/"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Page Facebook Go Helper"
                className="h-11 w-11 rounded-full bg-navy text-lime flex items-center justify-center hover:bg-[#0F2342] transition-colors hover:-translate-y-0.5 shadow-md"
                data-testid="fb-footer-link"
              >
                <Facebook size={20} fill="currentColor" strokeWidth={0} />
              </a>
              <a
                href="https://www.instagram.com/gohelperservice?igsh=ZWZuYXRxcnprd3V2"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Instagram Go Helper"
                className="h-11 w-11 rounded-full text-white flex items-center justify-center hover:-translate-y-0.5 transition-transform shadow-md"
                style={{ background: "linear-gradient(45deg, #f09433 0%, #e6683c 25%, #dc2743 50%, #cc2366 75%, #bc1888 100%)" }}
                data-testid="ig-footer-link"
              >
                <Instagram size={20} />
              </a>
            </div>
            <a
              href="mailto:gohelperservice@outlook.com"
              className="text-sm font-bold text-navy hover:underline"
              data-testid="email-footer-link"
            >
              gohelperservice@outlook.com
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
}

function Stat({ icon, value, suffix, label, testid }) {
  return (
    <div className="flex flex-col items-center md:items-start px-2 md:px-4" data-testid={testid}>
      <div className="flex items-center gap-2">
        {icon}
        <span className="font-heading font-black text-2xl md:text-4xl text-navy leading-none">
          {value}{suffix}
        </span>
      </div>
      <div className="text-xs md:text-sm font-bold text-[#475569] mt-1 uppercase tracking-wide text-center md:text-left">{label}</div>
    </div>
  );
}
